import { Dispatch, Ref, SetStateAction, memo, useCallback } from "react";
import { usePartialSubmitForOverview } from "src/apps/AppFlow/pages/hooks";
import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import { DrawerModal, FlowLayout, FlowLayoutItemsOverview } from "src/components";
import { Extractor } from "src/hooks/useReducerPageState";
import useSubpagesInArray, { ItemControl } from "src/hooks/useSubpages";
import { createStyles } from "src/styles";
import { Applicant, OtherOwnedProperty } from "src/types/Applicant";
import { BlockFocus } from "src/utilities/react";

import { formatters } from "@fraction/shared";

import MortgageInformationPages from "../MortgageInformation";

const { PROPERTY_USAGE_MAP } = formatters.enums;

type PickedProperties = "propertyLocation" | "propertyValue" | "propertyType";

interface OtherOwnedPropertiesProps {
  value?: ItemControl<Pick<OtherOwnedProperty, PickedProperties>>;
  onChange: Dispatch<SetStateAction<ItemControl<OtherOwnedProperty> | undefined>>;
  onNext: () => void;
  onBack: () => void;
  focusRef?: Ref<HTMLDivElement>;

  basePropertyPath: (number | string)[];
  extractor: Extractor<ApplicationFlowState>;
  state: ApplicationFlowState;

  title?: string;
  applicant?: Applicant;
  newItemTemplate?: Partial<OtherOwnedProperty>;

  // used by FlowLayout component
  blockFocus?: BlockFocus;
}

const styles = createStyles({
  drawer: {
    zIndex: 2,
  },
});

const OtherOwnedProperties = ({
  value,
  onChange,
  onNext,
  onBack,
  basePropertyPath,
  state,
  extractor,
  focusRef,
  title,
  applicant,
  newItemTemplate,
  ...props
}: OtherOwnedPropertiesProps) => {
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmitForOverview({ value, onNext });

  const headerFormatter = useCallback(
    (val: OtherOwnedProperty) =>
      val?.propertyLocation ? formatters.property.shortFormattedAddress(val.propertyLocation) : "Unknown",
    []
  );

  const subheaderFormatter = useCallback(
    (val: OtherOwnedProperty) =>
      val?.propertyType?.propertyUsage ? PROPERTY_USAGE_MAP[val.propertyType?.propertyUsage] : "",
    []
  );

  const descriptionFormatter = useCallback(
    (val: OtherOwnedProperty) =>
      formatters.number.getCurrencyFromNumber(val?.propertyValue, {
        inputInDollars: true,
      }),
    []
  );

  const currentlyEditing = value?.__editing !== undefined ? value?.items[value.__editing] : undefined;

  const { subpages, onCloseSubpages, onCompleteSubpages } = useSubpagesInArray({
    state,
    extractor,
    // we dont want the specific owned property that we are currently looking at for the property path,
    // so remove that index from the basePropertyPath array
    basePropertyPath: [...basePropertyPath.slice(0, -1)],
    renderers: {
      mortgages: {
        PageRenderer: MortgageInformationPages,
        properties: ["mortgageRateAndType", "mortgageBalanceAndLender", "mortgagePayments"],
        middleware: () => ({
          propertyAddress: currentlyEditing?.propertyLocation
            ? formatters.property.shortFormattedAddress(currentlyEditing?.propertyLocation)
            : "your property",
          isSubpage: true,
        }),
      },
    },
  });

  return (
    <>
      <FlowLayoutItemsOverview
        {...props}
        editContext={{ applicant }}
        focusRef={focusRef}
        newItemTemplate={newItemTemplate || {}}
        itemControl={value}
        exampleHeader="1234 Electronic Ave, Vancouver"
        exampleSubheader="Example"
        exampleDescription="$35,000.00"
        addItemButtonText="Add other owned property"
        headerFormatter={headerFormatter}
        subheaderFormatter={subheaderFormatter}
        descriptionFormatter={descriptionFormatter}
        onNext={handleNext}
        onBack={onBack}
        onChange={onChange}
        nextDisabled={nextDisabled}
        nextLoading={nextLoading}
        title={title || "Your other properties"}
        description="If you own any other properties other than the property you are applying to Fraction for, please add them here."
        skipButtonText="I have no other properties"
      />
      <DrawerModal style={styles.drawer} mounted={subpages !== undefined}>
        <FlowLayout onComplete={onCompleteSubpages} pages={subpages} onClose={onCloseSubpages} />
      </DrawerModal>
    </>
  );
};

export default memo(OtherOwnedProperties);
